import React, { useEffect, useState } from "react";

import _ from 'lodash';
import cx from 'classnames';
import { Carousel } from "react-bootstrap";

import { CallSettings } from "./components/callSettings";
import { ScenarioSettings } from "./components/scenarios";
import { SimulationContext, SimulationContextProps } from "./context";
import { animated, useTransition } from "@react-spring/web";
import { PersonaEditor } from "./components/scenarios/persona";

import styles from "./edit.module.scss";
import { SimulationMenuBar, ValidationErrorDialog } from "../components/menu";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AssessmentConfiguration, AssessmentParameter, Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { discardSimulationDraft, editSimulation, fetchDraftSimulation, invalidatePublishSimulation, publishSimulation, updateSimulation, updateValidationErrorsSimulation } from "src/redux/actions/coaching/simulation";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import { prepareSimulationRequest, validateSimulationRequest } from "./helper";
import { OrganizationEditor } from "./components/scenarios/organization";
import { ObjectivesEditor } from "./components/scenarios/objectives";
import { AssessmentSettings } from "./components/assessments";
import { AssessmentParameterEditor } from "./components/assessments/editor";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { SimulationNotFound } from "src/pages/coaching/components/notFound";
import Wizard from "src/components/wizard";
import AssessmentConfigurations from "./components/assessmentConfiguration";
import { SaveLearningsDialog } from "../../courses/edit/components/menu";
import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import { fetchAssessmentConfigurations, invalidateAssessmentConfigurations } from "src/redux/actions/coaching/settings";

const defaultSimulation = "/images/assets/simulationThumbnail.jpg";

export const EditSimulationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const backPath = location.state?.from;

  const params = useParams();
  const id = params.id;

  const persistedSimulation: Simulation = useSelector(
    (state: any) => _.get(state, `coaching.simulations.simulation[${id}].item`) as Simulation,
  );

  const loading = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${id}].loading`);
  });

  const simulation: Simulation = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.edit.item`) as Simulation;
  });

  const scorecards = useSelector((state: any) =>
    _.get(state, "coaching.settings.scorecards.items", [])
  );

  const simulationErrorCode = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.simulation[${id}].error.errors[0].code`);
  });

  const simulationUpdated = !_.isEqual(_.omit(simulation,'lastPublished','hasChanges','status','modifiedDate','publishedBy'),
    _.omit(persistedSimulation,'lastPublished','hasChanges','status','modifiedDate','publishedBy'));


  const publishing = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.publish.publishing`);
  });

  const published = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.publish.published`);
  });


  const hasChanges = React.useMemo(() => {
    return _.get(simulation, `hasChanges`) as boolean;
  }, [simulation]);

  const [currentStep, setCurrentStep] = React.useState(0);

  const [editPersona, setEditPersona] = React.useState(false);
  const [editObjectives, setEditObjectives] = React.useState(false);
  const [editOrganizationalInfo, setEditOrganizationalInfo] = React.useState(false);
  const [editAssessmentParamater, setEditAssessmentParamater] = React.useState<AssessmentParameter | null>(null);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = React.useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeAssessmentScorecard, setActiveAssessmentScorecard] = useState<AssessmentConfiguration | null>(
    simulation?.assessmentConfiguration || null
  );
  const [isPublished, setIsPublished] = useState(false);

  const [contextState, setContextState] = useState<SimulationContextProps>({});

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const [showSaveChangesDialog, setShowSaveChangesDialog] = React.useState(false);

  const personaTransition = useTransition(editPersona, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const organizationTransition = useTransition(editOrganizationalInfo, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const objectivesTransition = useTransition(editObjectives, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const assessmentTransition = useTransition(editAssessmentParamater, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const settingsTransition = useTransition(showSettings, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
  });

  const handleEditPersona = (value: boolean) => {
    setEditPersona(value);
  }

  const handleEditOrganizationalInfo = (value: boolean) => {
    setEditOrganizationalInfo(value);
  }

  const handleEditObjectives = (value: boolean) => {
    setEditObjectives(value);
  }

  const handleEditAssessmentParameter = (value: AssessmentParameter | null) => {
    setEditAssessmentParamater(value);
  }

  const handleEditAssessmentScoreCard = (value: boolean) => {
    setShowSettings(value);
  };

  const handleSelectAssessmentScoreCard = (scorecard: AssessmentConfiguration | null) => {
    setActiveAssessmentScorecard(scorecard);
  };

  const handleNextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };


  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handleGoToError = (error?: SimulationValidationError) => {
    setContextState((prev) => {
      return {
        ...prev,
        validationError: error,
      };
    });
  };

  const handleSave = () => {
    const validationErrors = validateSimulationRequest(simulation);

    if (_.size(validationErrors) > 0) {
      dispatch(updateValidationErrorsSimulation({
        simulation: simulation.id!,
        errors: validationErrors,
      }));
    }

    const request = prepareSimulationRequest(persistedSimulation, simulation);

    dispatch(
      updateSimulation({
        id: simulation.id!,
        request: request,
      }),
    );
  };

  const handlePublish = () => {
    const validationErrors = validateSimulationRequest(simulation);
  
    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsSimulation({
          simulation: simulation.id!,
          errors: validationErrors,
        }),
      );
  
      setShowValidationErrorDialog(true);
      return;
    }
  
    dispatch(publishSimulation(simulation.id!));
  };

  const onBackClick = () => {
    if (simulationUpdated) {
      setShowSaveChangesDialog(true);
      return;
    }
  
    if (backPath) {
      navigate(backPath);
    } else {
      navigate("/coaching/manage");
    }
  };


  useEffect(() => {
    setContextState({
      gotoError: handleGoToError,
      editPersona: handleEditPersona,
      editObjectives: handleEditObjectives,
      editOrganizationInfo: handleEditOrganizationalInfo,
      editAssessmentParameter: handleEditAssessmentParameter,
      editAssessmentScoreCard: handleEditAssessmentScoreCard,
      setActiveAssessmentScorecard: handleSelectAssessmentScoreCard
    });

    return () => {
      dispatch(invalidateAssessmentConfigurations());
    };
  }, []);

  useEffect(() => {
    setContextState(prev => ({
      ...prev,
      activeAssessmentScorecard,
    }));
  }, [activeAssessmentScorecard]);


  useEffect(() => {
    if (id) {
      dispatch(fetchDraftSimulation(id));

      const courseLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };

      dispatch(fetchCourseLearners(courseLearnerRequest));
    }

    setContextState((prev) => {
      return {
        ...prev,
        id: id,
        gotoError: handleGoToError,
        editPersona: handleEditPersona,
        editObjectives: handleEditObjectives,
        editOrganizationInfo: handleEditOrganizationalInfo,
        editAssessmentScoreCard: handleEditAssessmentScoreCard
      };
    });
  }, [id]);

  useEffect(() => {
    if (persistedSimulation && persistedSimulation.id !== simulation?.id) {
      dispatch(editSimulation(persistedSimulation));
    }
  }, [persistedSimulation, simulation]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);

  useEffect(() => {
    if (!simulation) return;

    dispatch(fetchAssessmentConfigurations({callType: 'all', status: "published"}));

    setActiveAssessmentScorecard(
      simulation.assessmentConfiguration ?? null
    );
  }, [simulation?.id]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if (showSettings) {
      const el = document.getElementById("app-body");
      el?.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [showSettings]);

  useEffect(() => {
    if (contextState.validationError) {
      const type = contextState.validationError.type;

      switch (type) {
        case "call-settings": {
          setCurrentStep(0);
          break;
        }

        case "organization": {
          setCurrentStep(1);
          setEditOrganizationalInfo(true);
          break;
        }

        case "persona": {
          setCurrentStep(1);
          setEditPersona(true);
          break;
        }

        case "objectives": {
          setCurrentStep(1);
          setEditObjectives(true);
          break;
        }

        default: {
          break;
        }
      }
    }
  }, [contextState.validationError]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (simulationErrorCode === 10007) {
    return <SimulationNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && simulation?.id != id) || !simulation) {
    return <div className={styles.loading}>Loading simulation...</div>
  }

  return (
    <SimulationContext.Provider value={contextState}>
       <div
        className={cx(
          showSettings ? styles.pageWrapper : styles.wrapper
        )}
      >

      <div
        className={cx(styles.createSimulationContainer, {
          [styles.noScroll]:
            editPersona ||
            editObjectives ||
            editOrganizationalInfo ||
            editAssessmentParamater ||
            showSettings,
          [styles.publishMode]: isPublished 
        })}
      >
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.backButton}
              onClick={onBackClick}
            >
              <i className="paperflite-left-arrow"></i>
              <div>
                <span className={styles.back}>back</span>{" "}
              </div>
            </div>
          </div>
          <div className={styles.menubar}>
            <SimulationMenuBar
              simulation={simulation}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
              simulationUpdated={simulationUpdated}
            />
          </div>
        </div>
        <div>
          <CarouselNavigation
            currentStep={currentStep}
            onNextStep={handleNextStep}
            onPrevStep={handlePrevStep}
            onPublish={handlePublish}
            publishing={publishing}
            callType={simulation?.type}
            canPublish={hasChanges && !simulationUpdated}
          >
            <CallSettings simulation={simulation} />
            <ScenarioSettings simulation={simulation} />
            <AssessmentSettings simulation={simulation} />
          </CarouselNavigation>
        </div>
        <PublishView isOpen={isPublished} simulation={simulation} onClose={() => setIsPublished(false)} callType={simulation?.type} />
        <ValidationErrorDialog
          show={showValidationErrorDialog}
          onClose={() => setShowValidationErrorDialog(false)}
        />
      </div>

      {organizationTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <OrganizationEditor simulation={simulation} />
          </animated.div>
        ) : (
          false
        );
      })}

      {personaTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <PersonaEditor simulation={simulation} />
          </animated.div>
        ) : (
          false
        );
      })}

      {objectivesTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <ObjectivesEditor simulation={simulation} />
          </animated.div>
        ) : (
          false
        );
      })}

      {assessmentTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.personaEditor)}>
            <AssessmentParameterEditor
              simulation={simulation}
              assessmentParameter={item}
            />
          </animated.div>
        ) : (
          false
        );
      })}

      {settingsTransition((style, item) =>
        item ? (
          <animated.div style={style} className={styles.settingsOverlay}>
            <AssessmentConfigurations
              simulation={simulation}
            />
          </animated.div>
        ) : false
      )}
      </div>

      {showSaveChangesDialog && (
        <SaveLearningsDialog
          show={showSaveChangesDialog}
          course={simulation}
          onFinish={() => setShowSaveChangesDialog(false)}
          onClose={() => setShowSaveChangesDialog(false)}
          handleSave={handleSave}
          type="simulation"
        />
      )}
    </SimulationContext.Provider>
  );
};
interface CarouselNavigationProps {
  currentStep: number;
  onNextStep?: () => void;
  onPrevStep: () => void;
  onPublish?: () => void;
  publishing?: boolean;
  children: React.ReactNode;
  gap?: number;
  callType?: string;
  canPublish?: boolean;
}

const CarouselNavigation: React.FC<CarouselNavigationProps> = ({ 
  currentStep, 
  onNextStep, 
  onPrevStep, 
  onPublish, 
  publishing = false,
  children, 
  gap = 40,
  callType,
  canPublish
}) => {
  const totalSteps = callType === "elevator_pitch" ? 1 : 2;
  const isLastStep = currentStep === totalSteps - 1;
  const childrenArray = React.Children.toArray(children);
  
  const stepLabels = ["Set up scenario", "Publish now"];

  return (
    <div className={styles.carouselNavigation}>
      <Carousel
        className={styles.carousel}
        activeIndex={currentStep}
        controls={false}
        indicators={false}
        interval={null}
      >
        {childrenArray.map((child, index) => (
          <Carousel.Item
            key={index}
            style={{ paddingLeft: gap, paddingRight: gap }}
          >
            {child}
          </Carousel.Item>
        ))}
      </Carousel>

      <div className={styles.navigationContainer}>
        <div className={styles.left}>
          {currentStep > 0 && (
            <button className={styles.previous} onClick={onPrevStep}>
              <i className="paperflite-left-arrow"></i>
            </button>
          )}
        </div>

        <div className={styles.center}>
          {!isLastStep && (
            <button className={styles.primary} onClick={onNextStep}>
              <span className={styles.indicator}>
                Next ({currentStep + 1}/{totalSteps}) :{" "}
              </span>
              {stepLabels[currentStep + 1] && (
                <span className={styles.label}>
                  {stepLabels[currentStep]}
                </span>
              )}
              <i className="paperflite-right-arrow"></i>
            </button>
          )}

          {isLastStep && onPublish && (
            <button
              className={styles.primary}
              onClick={onPublish}
              disabled={publishing || !canPublish}
            >
              <span className={styles.label}>
                {publishing ? "Publishing..." : "Publish now"}
              </span>
              <i className="paperflite-right-arrow"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface PublishViewProps {
  isOpen: boolean;
  simulation: Simulation;
  onClose: () => void;
  callType: string | undefined;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  simulation,
  onClose,
  callType
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        onClose();
      }
    },
  });

  useEffect(() => {
    return () => {
      dispatch(invalidatePublishSimulation());
    };
  }, []);

  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <div className={styles.left}>
            <Thumbnail className={cx(styles.thumbnail, {
              [styles.discovery]: callType === "discovery",
              [styles.coldcall]: callType === "cold_call",
              [styles.elevatorPitch]: callType === "elevator_pitch",
            })} media={simulation.banner?.background} alt="Persona" position={simulation.banner?.position} crop={simulation.banner?.crop} width={200} defaultImage={defaultSimulation} />
            <img className={styles.title} src="/images/coaching/congratulations.svg" alt="Congratulations" />
            <p className={styles.message}>
              you’ve successfully published the simulation “{simulation.name || 'Untitled Simulation'}”.
            </p>
            <div className={styles.actions}>
              <Button
                className={cx(styles.action, styles.primary)}
                variant="primary"
                icon="paperflite-right-arrow"
                text="view simulation details"
                onClick={() => setClosePublish(true)}
              />
              <Button
                className={styles.action}
                variant="secondary"
                icon="paperflite-close"
                text="exit"
                onClick={() => navigate(`/coaching/manage`)}
              />
            </div>
          </div>
          <div className={styles.right}>
            <img className={styles.illustration} src="/images/coaching/confetti.gif" />
          </div>
        </animated.div>
      ),
  );
};